//Interactive Dashboard with real data
import React, { useState, useEffect } from 'react'
import { CheckCircle, Clock, ArrowUpRight, ArrowDownRight,  UserCircle, Mail, Plus,  Send } from "lucide-react"
import { useAuth } from '../Contexts/AuthContext'
import TokenChecker from './TokenChecker'
import { error as logError } from "../utils/logger";

const StatCard = ({ title, value, icon: Icon, change, isIncrease, onClick }) => (
  <div className="bg-white p-6 rounded-lg shadow-sm  " onClick={onClick}>
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-lg font-semibold text-[#2D3748]">{title}</h3>
      <Icon className="h-6 w-6 text-[#4299E1]" />
    </div>
    <p className="text-3xl font-bold text-[#2D3748]">{value}</p>
    {change && (
      <div className={`flex items-center mt-2 ${isIncrease ? 'text-green-500' : 'text-red-500'}`}>
        {isIncrease ? <ArrowUpRight className="h-4 w-4 mr-1" /> : <ArrowDownRight className="h-4 w-4 mr-1" />}
        <span className="text-sm">{change}</span>
      </div>
    )}
  </div>
)

const TaskProgressChart = ({ stats }) => {
  const total = stats.totalTasks || 1 // Avoid division by zero
  const completed = ((stats.completedTasks || 0) / total) * 100
  const inProgress = ((stats.inProgressTasks || 0) / total) * 100
  const notStarted = ((stats.notStartedTasks || 0) / total) * 100

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm">
      <h3 className="text-lg font-semibold text-[#2D3748] mb-4">Task Progress Overview</h3>
      
      {/* Simple visual representation */}
      <div className="relative w-48 h-48 mx-auto mb-4">
        <div className="w-full h-full rounded-full bg-gray-200 relative overflow-hidden">
          {/* Completed section */}
          <div 
            className="absolute inset-0 rounded-full bg-green-500"
            style={{
              clipPath: `polygon(50% 50%, 50% 0%, ${50 + (completed/100) * 50}% 0%, 50% 50%)`
            }}
          />
          {/* In Progress section */}
          <div 
            className="absolute inset-0 rounded-full bg-blue-500"
            style={{
              clipPath: `polygon(50% 50%, ${50 + (completed/100) * 50}% 0%, ${50 + ((completed + inProgress)/100) * 50}% 0%, 50% 50%)`
            }}
          />
        </div>
        
        {/* Center circle with total count */}
        <div className="absolute inset-1/4 rounded-full bg-white flex items-center justify-center border-4 border-gray-100">
          <div className="text-center">
            <div className="text-2xl font-bold text-[#2D3748]">{stats.totalTasks}</div>
            <div className="text-xs text-[#4A5568]">Total Tasks</div>
          </div>
        </div>
      </div>

      <div className="flex justify-between">
        <div className="text-center">
          <div className="flex items-center justify-center mb-1">
            <div className="w-3 h-3 bg-green-500 rounded-full mr-2" />
            <div className="text-sm font-medium text-[#4A5568]">Completed</div>
          </div>
          <div className="text-lg font-semibold text-[#2D3748]">{Math.round(completed)}%</div>
          <div className="text-sm text-[#718096]">({stats.completedTasks})</div>
        </div>
        <div className="text-center">
          <div className="flex items-center justify-center mb-1">
            <div className="w-3 h-3 bg-blue-500 rounded-full mr-2" />
            <div className="text-sm font-medium text-[#4A5568]">In Progress</div>
          </div>
          <div className="text-lg font-semibold text-[#2D3748]">{Math.round(inProgress)}%</div>
          <div className="text-sm text-[#718096]">({stats.inProgressTasks})</div>
        </div>
        <div className="text-center">
          <div className="flex items-center justify-center mb-1">
            <div className="w-3 h-3 bg-gray-400 rounded-full mr-2" />
            <div className="text-sm font-medium text-[#4A5568]">Not Started</div>
          </div>
          <div className="text-lg font-semibold text-[#2D3748]">{Math.round(notStarted)}%</div>
          <div className="text-sm text-[#718096]">({stats.notStartedTasks})</div>
        </div>
      </div>
    </div>
  )
}

const RecentActivity = ({ recentTasks }) => (
  <div className="bg-white p-6 rounded-lg shadow-sm">
    <h3 className="text-lg font-semibold text-[#2D3748] mb-4">Recent Activity</h3>
    <div className="max-h-64 overflow-y-auto">
      {recentTasks.length === 0 ? (
        <p className="text-[#718096] text-center py-8">No recent activity</p>
      ) : (
        <ul className="space-y-4">
          {recentTasks.map((task, index) => (
            <li key={task.id || index} className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-[#2D3748] truncate max-w-xs">{task.title}</p>
                <p className="text-xs text-start text-[#718096]">{task.timeAgo}</p>
              </div>
              <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                task.completed 
                  ? 'bg-green-100 text-green-700' 
                  : task.progress > 0 
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-gray-100 text-gray-700'
              }`}>
                {task.completed ? 'Completed' : task.progress > 0 ? `${task.progress}%` : 'Not Started'}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  </div>
)

const UpcomingDeadlines = ({ upcomingTasks }) => (
  <div className="bg-white p-6 rounded-lg shadow-sm">
    <h3 className="text-lg font-semibold text-[#2D3748] mb-4">Upcoming Deadlines</h3>
    <div className="max-h-64 overflow-y-auto">
      {upcomingTasks.length === 0 ? (
        <p className="text-[#718096] text-center py-8">No upcoming deadlines</p>
      ) : (
        <ul className="space-y-4">
          {upcomingTasks.map((task, index) => (
            <li key={task.id || index} className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-[#2D3748] truncate max-w-xs">{task.title}</p>
                <p className="text-xs text-start text-[#718096]">{task.dueIn}</p>
              </div>
              <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                task.priority === 'Immediate' 
                  ? 'bg-red-100 text-red-700' 
                  : task.priority === 'Moderate'
                    ? 'bg-yellow-100 text-yellow-700'
                    : 'bg-green-100 text-green-700'
              }`}>
                {task.priority}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  </div>
)

const Button = ({ variant = "default", className = "", children, onClick, ...props }) => {
  const baseStyles = "px-4 py-2 rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#4299E1] transition-colors"
  const variantStyles = {
    default: "bg-[#4299E1] text-white hover:bg-[#3182CE]",
    outline: "border border-[#4299E1] text-[#4299E1] hover:bg-[#EBF8FF]",
    ghost: "text-[#4A5568] hover:text-[#4299E1] hover:bg-[#EBF8FF]"
  }
  
  return (
    <button 
      className={`${baseStyles} ${variantStyles[variant]} ${className}`} 
      onClick={onClick}
      {...props}
    >
      {children}
    </button>
  )
}

const Dashboard = ({ onCategorySelect }) => {
  const [stats, setStats] = useState({
    totalTasks: 0,
    personalTasks: 0,
    assignedTasks: 0,
    invitedTasks: 0,
    completedTasks: 0,
    inProgressTasks: 0,
    notStartedTasks: 0
  })
  const [recentTasks, setRecentTasks] = useState([])
  const [upcomingTasks, setUpcomingTasks] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  
  const { authData } = useAuth()

  useEffect(() => {
    if (authData?.email) {
      fetchDashboardData()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authData])

  const fetchDashboardData = async () => {
    setLoading(true)
    setError(null)
    
    try {
      // Fetch all task categories
      const [personalRes, assignedRes, invitedRes] = await Promise.all([
        TokenChecker('/api/tasks/personal/', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userEmail: authData.email })
        }),
        TokenChecker('/get_assigned/', {
          method: 'POST', 
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userEmail: authData.email })
        }),
        TokenChecker('/get_invited/', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userEmail: authData.email })
        })
      ])

      const personalData = await personalRes.json()
      const assignedData = await assignedRes.json()
      const invitedData = await invitedRes.json()

      const personalTasks = personalData.tasks || []
      const assignedTasks = (assignedData.tasks || []).filter(task => task.owner !== authData.email)
      const invitedTasks = (invitedData.tasks || []).filter(task => task.owner !== authData.email)
      
      const allTasks = [...personalTasks, ...assignedTasks, ...invitedTasks]
      
      // Calculate statistics
      const completed = allTasks.filter(task => task.completed).length
      const inProgress = allTasks.filter(task => !task.completed && (task.progress > 0)).length
      const notStarted = allTasks.filter(task => !task.completed && (!task.progress || task.progress === 0)).length

      setStats({
        totalTasks: allTasks.length,
        personalTasks: personalTasks.length,
        assignedTasks: assignedTasks.length,
        invitedTasks: invitedTasks.length,
        completedTasks: completed,
        inProgressTasks: inProgress,
        notStartedTasks: notStarted
      })

      // Get recent activity (last 10 tasks, sorted by creation date)
      const recentActivity = allTasks
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 10)
        .map(task => ({
          ...task,
          timeAgo: getTimeAgo(task.created_at)
        }))
      
      setRecentTasks(recentActivity)

      // Get upcoming deadlines (next 10 uncompleted tasks, sorted by due date)
      const upcoming = allTasks
        .filter(task => !task.completed)
        .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))
        .slice(0, 10)
        .map(task => ({
          ...task,
          dueIn: getDueInText(task.dueDate)
        }))
      
      setUpcomingTasks(upcoming)

    } catch (err) {
      logError('Error fetching dashboard data:', err)
      setError('Failed to load dashboard data')
    } finally {
      setLoading(false)
    }
  }

  const getTimeAgo = (dateString) => {
    // Handle different date formats
    const parseDate = (dateStr) => {
      if (!dateStr) return null
      
      // Try direct Date parsing first (works for ISO strings)
      const directDate = new Date(dateStr)
      if (!isNaN(directDate.getTime())) {
        return directDate
      }
      
      return null
    }
    
    const date = parseDate(dateString)
    if (!date) return 'Unknown time'
    
    const now = new Date()
    const diffInMs = now.getTime() - date.getTime()
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60))
    
    if (diffInHours < 1) return 'Just now'
    if (diffInHours < 24) return `${diffInHours} hours ago`
    if (diffInHours < 48) return '1 day ago'
    return `${Math.floor(diffInHours / 24)} days ago`
  }

  const getDueInText = (dueDateString) => {
    // Handle different date formats like your TaskCard does
    const parseDate = (dateStr) => {
      if (!dateStr) return null
      
      // Try direct Date parsing first
      const directDate = new Date(dateStr)
      if (!isNaN(directDate.getTime())) {
        return directDate
      }
      
      // Handle "Month D, YYYY" format
      const months = {
        'January': 0, 'February': 1, 'March': 2, 'April': 3, 'May': 4, 'June': 5,
        'July': 6, 'August': 7, 'September': 8, 'October': 9, 'November': 10, 'December': 11
      }
      
      const parts = dateStr.split(/[\s,]+/)
      if (parts.length >= 3) {
        const month = months[parts[0]]
        const day = parseInt(parts[1].replace(/\D/g, ''))
        const year = parseInt(parts[2])
        
        if (month !== undefined && !isNaN(day) && !isNaN(year)) {
          return new Date(year, month, day)
        }
      }
      
      return null
    }
    
    const dueDate = parseDate(dueDateString)
    if (!dueDate) return 'Invalid date'
    
    const now = new Date()
    const diffInMs = dueDate.getTime() - now.getTime()
    const diffInDays = Math.ceil(diffInMs / (1000 * 60 * 60 * 24))
    
    if (diffInDays < 0) return `${Math.abs(diffInDays)} days overdue`
    if (diffInDays === 0) return 'Due today'
    if (diffInDays === 1) return 'Due tomorrow'
    return `Due in ${diffInDays} days`
  }

  const handleQuickAction = (action) => {
    if (!onCategorySelect) return
    switch (action) {
      case 'create':
        onCategorySelect('Personal Tasks', 'create-task')
        break
      case 'invite':
        onCategorySelect('Tasks Delegated - Invitation', 'create-task')
        break
      case 'assign':
        onCategorySelect('Tasks Delegated - Assigned', 'create-task')
        break
      default:
        break
    }
  }

  if (loading) {
    return (
      <div className="bg-[#F7FAFC] min-h-screen p-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-[#4A5568]">Loading dashboard...</div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-[#F7FAFC] min-h-screen p-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-red-500">{error}</div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-[#F7FAFC] min-h-screen flex flex-col">
     <div className='flex-1 p-8'>
      <header className="flex justify-center items-center mb-2">
        <div className='text-center' >
          <h1 className="text-4xl font-semibold text-[#2D3748]">Dashboard</h1>
          <p className="text-[#2D3748] mt-2">Welcome {authData.firstName}! Here's your task overview.</p>
        </div>
        {/*<Button onClick={() => window.print()}>Generate Report</Button>*/}
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard 
          title="Total Tasks" 
          value={stats.totalTasks} 
          icon={CheckCircle} 
          onClick={() => onCategorySelect && onCategorySelect('Personal Tasks')}
        />
        <StatCard 
          title="Personal Tasks" 
          value={stats.personalTasks} 
          icon={UserCircle} 
          onClick={() => onCategorySelect && onCategorySelect('Personal Tasks')}
        />
        <StatCard 
          title="Invited Tasks" 
          value={stats.invitedTasks} 
          icon={Mail} 
          onClick={() => onCategorySelect && onCategorySelect('Invited Tasks')}
        />
        <StatCard 
          title="Assigned Tasks" 
          value={stats.assignedTasks} 
          icon={Clock} 
          onClick={() => onCategorySelect && onCategorySelect('Assigned Tasks')}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <TaskProgressChart stats={stats} />
        <RecentActivity recentTasks={recentTasks} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <UpcomingDeadlines upcomingTasks={upcomingTasks} />
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <h3 className="text-lg font-semibold text-[#2D3748] mb-4">Quick Actions</h3>
          <div className="space-y-4">
            <Button 
              className="w-full flex items-center justify-center gap-2" 
              onClick={() => handleQuickAction('create')}
            >
              <Plus className="h-4 w-4" />
              Create New Task
            </Button>
            <Button 
              variant="outline" 
              className="w-full flex items-center justify-center gap-2" 
              onClick={() => handleQuickAction('invite')}
            >
              <Mail className="h-4 w-4" />
              Send A Task Invite
            </Button>
            <Button 
              variant="outline" 
              className="w-full flex items-center justify-center gap-2" 
              onClick={() => handleQuickAction('assign')}
            >
              <Send className="h-4 w-4" />
              Assign A Task
            </Button>
          </div>
        </div>
      </div>
      </div>
    </div>
  )
}

export default Dashboard
